import React from 'react';
import { Download, Check, Lock, ShieldCheck, KeyRound, Server, ScrollText, Scale } from 'lucide-react';

const FlyerPage = () => {
  const handleDownload = () => {
    window.print();
  };

  const features = [
    'více než 100 pedagogických šablon pro MŠ, ZŠ i SŠ',
    'přípravy na hodinu, IVP, pracovní listy, testy i slovní hodnocení',
    'průběžné aktualizace podle RVP 2025',
    'export do PDF a DOCX',
    'AI asistenty pracující jen s dokumenty vaší školy',
    'překlady ve více než 50 jazycích se znalostí školské terminologie',
    'generování ilustrací, diagramů i krátkých vysvětlovacích videí',
    'detekci AI generovaných textů',
    'historii materiálů, sdílenou knihovnu a přehledné vyhledávání',
  ];

  const security = [
    { icon: Lock, text: 'přístup pouze k dokumentům vaší školy' },
    { icon: KeyRound, text: 'plná kontrola nad přístupy' },
    { icon: Server, text: 'data vaší školy ani výstupy pedagogů neslouží k trénování modelu' },
    { icon: ShieldCheck, text: 'šifrovaná komunikace' },
    { icon: ScrollText, text: 'audit log aktivit' },
    { icon: Scale, text: 'soulad s požadavky GDPR' },
  ];

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #flyer-content, #flyer-content * { visibility: visible !important; }
          #flyer-content {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          #flyer-content a { text-decoration: none !important; color: inherit !important; }
          #download-bar { display: none !important; }
          nav, footer { display: none !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>

      {/* Download button */}
      <div id="download-bar" className="fixed top-20 right-6 z-50">
        <button
          onClick={handleDownload}
          data-testid="download-pdf-btn"
          className="flex items-center gap-2 px-6 py-3 bg-[#008B8B] hover:bg-[#007272] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          <Download className="w-5 h-5" />
          Stáhnout PDF
        </button>
      </div>

      {/* Page wrapper */}
      <div className="min-h-screen bg-gray-100 pt-24 pb-12 px-4 flex justify-center">
        {/* A4 Flyer — flex column to fill the page */}
        <div
          id="flyer-content"
          className="bg-white shadow-2xl flex flex-col"
          style={{ width: '210mm', height: '297mm', maxWidth: '100%' }}
        >
          {/* ===== HEADER ===== */}
          <div className="relative overflow-hidden flex-shrink-0" style={{ backgroundColor: '#008B8B', padding: '30px 38px 26px' }}>
            <div className="absolute top-0 right-0 w-56 h-56 rounded-full opacity-10" style={{ backgroundColor: '#00A3A3', transform: 'translate(30%, -40%)' }} />
            <div className="absolute bottom-0 left-0 w-36 h-36 rounded-full opacity-10" style={{ backgroundColor: '#00A3A3', transform: 'translate(-30%, 40%)' }} />

            <div className="relative flex items-center justify-between">
              <img
                src="https://customer-assets.emergentagent.com/job_smartedu-ai-4/artifacts/2da2chy6_skolobotlogo.png"
                alt="Školobot"
                className="h-9"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <div className="text-right text-white/80" style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px' }}>
                <div>www.skolobot.cz</div>
                <div>info@skolobot.cz · +420 583 033 357</div>
              </div>
            </div>

            <div className="relative mt-5">
              <h1 className="text-white font-bold leading-tight" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '27px' }}>
                Proč platit 5 různých AI nástrojů?
              </h1>
            </div>
          </div>

          {/* ===== CONTENT — flex column distributes space evenly ===== */}
          <div className="flex-1 flex flex-col" style={{ padding: '28px 38px 24px', gap: '20px' }}>

            {/* Problem */}
            <div className="flex-1">
              <h2 className="font-bold" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '16px', color: '#1E293B', marginBottom: '8px' }}>
                Standardní AI nástroje ve škole nestačí.
              </h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#475569', lineHeight: '1.7' }}>
                Neznají české školství, nerozumí RVP 2025 a nepracují s&nbsp;vaším ŠVP ani školním řádem. Často tak dávají nepřesné výstupy, které míjejí realitu každodenní pedagogické práce.
              </p>
            </div>

            {/* Solution — green box */}
            <div className="rounded-xl flex-1" style={{ backgroundColor: '#E6F4F4', border: '1.5px solid #008B8B', padding: '22px 24px' }}>
              <h2 className="font-bold" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '16px', color: '#007272', marginBottom: '8px' }}>
                Školobot je vyladěný pro české školství.
              </h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#475569', lineHeight: '1.7' }}>
                Dává <strong style={{ color: '#1E293B' }}>pedagogům i vedení školy</strong> vše potřebné pro <strong style={{ color: '#1E293B' }}>přípravu výuky</strong>, <strong style={{ color: '#1E293B' }}>tvorbu materiálů</strong>, <strong style={{ color: '#1E293B' }}>hodnocení žáků</strong> i <strong style={{ color: '#1E293B' }}>administrativu</strong>. V&nbsp;jednom prostředí, <strong style={{ color: '#1E293B' }}>na fakturu</strong> a za cenu, která dává škole smysl.
              </p>
            </div>

            {/* Features list */}
            <div className="flex-[2]">
              <h2 className="font-bold" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '17px', color: '#1E293B', marginBottom: '16px' }}>
                Co všechno Školobot zvládne?
              </h2>
              <div style={{ columns: '2', columnGap: '30px' }}>
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5" style={{ breakInside: 'avoid', marginBottom: '12px' }}>
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#008B8B' }} />
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12.5px', color: '#1E293B', lineHeight: '1.55' }}>
                      {f}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Security */}
            <div className="rounded-xl flex-1" style={{ backgroundColor: '#F9FAFB', border: '1.5px solid #E2E8F0', padding: '22px 24px' }}>
              <h2 className="font-bold" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '17px', color: '#1E293B', marginBottom: '14px' }}>
                Bezpečnost a kontrola
              </h2>
              <div style={{ columns: '2', columnGap: '30px' }}>
                {security.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="flex items-start gap-2.5" style={{ breakInside: 'avoid', marginBottom: '12px' }}>
                      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#008B8B' }} />
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12.5px', color: '#475569', lineHeight: '1.55' }}>
                        {s.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div className="rounded-xl text-center" style={{ backgroundColor: '#008B8B', padding: '28px 32px' }}>
              <h3 className="text-white font-bold" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '18px' }}>
                Více informací
              </h3>
              <a
                href="https://www.skolobot.cz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-lg font-bold no-underline hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '16px', backgroundColor: '#F5B700', color: '#1E293B', padding: '12px 40px', marginTop: '14px', textDecoration: 'none' }}
              >
                www.skolobot.cz
              </a>
            </div>
          </div>

          {/* ===== FOOTER ===== */}
          <div className="flex-shrink-0" style={{ padding: '14px 38px', borderTop: '1.5px solid #E2E8F0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: '#64748B' }}>
                THEBIGDOT s.r.o. · IČO: 19062044 · Zámostní 1155/27, 710 00 Ostrava
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: '#64748B' }}>
                +420 583 033 357 · info@skolobot.cz
              </div>
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '9px', color: '#94A3B8', marginTop: '4px', textAlign: 'center' }}>
              Součást skupiny vzdělávací agentury GAVITO technic s.r.o.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FlyerPage;
